import type { GeoJsonObject, Feature, FeatureCollection } from 'geojson';

/**
 * Removes null and undefined values from an object
 */
function removeNullUndefined(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== null && value !== undefined) {
      result[key] = value;
    }
  });

  return result;
}

/**
 * Removes all properties with null or undefined values from GeoJSON data
 * @param data - GeoJSON object or array of features
 * @returns Normalized GeoJSON with null/undefined properties removed
 */
export default function getNormalizedMapData(
  data: GeoJsonObject,
): GeoJsonObject {
  if (!data) return data;

  // Handle FeatureCollection
  if (data.type === 'FeatureCollection') {
    return {
      ...data,
      features: (data as FeatureCollection).features.map(
        (feature: Feature) => ({
          ...feature,
          properties: feature.properties
            ? removeNullUndefined(feature.properties)
            : feature.properties,
        }),
      ),
    };
  }

  // Handle single Feature
  if (data.type === 'Feature') {
    return {
      ...data,
      properties: (data as Feature).properties
        ? removeNullUndefined(
            (data as Feature).properties as Record<string, unknown>,
          )
        : (data as Feature).properties,
    };
  }

  // Return other geometry types as-is
  return data;
}
